// CommonSKillVationLoginForm.jsx
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SkillVationAuthLayout from "../../../layout/SkillVationAuthLayout";
import useAuthStore from "../../../shared/store/useAuthStore";
import { useToaster } from "../../../shared/utils/ToasterProvider";
import { fetchData as fetchDataNode } from "../../../assets/nodeApi/fetchData";
import { OrDivider } from "../../../shared/utils/CustomDivider";
import useRoleCheck from "./useUserRoleCheck";
import { postLogin, LOGIN_ROUTE_BY_ROLE } from "../../../assets/nodeApi/postApi/postSchoolApi";
import { getLastVisitedPath } from "../../../shared/utils/localstorage";
import RoleSelectionModal from "./RoleSectionModal";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#9D9D9D",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#9D9D9D",
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4E93E1",
      borderWidth: "2px",
    },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
  },
};

const submitButtonStyle = {
  height: 48,
  borderRadius: "8px",
  backgroundColor: "#4E93E1",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "16px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#2563EB",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#4E93E1",
    opacity: 0.6,
    color: "#fff",
  },
  color: '#fff'
};

const CommonSKillVationLoginForm = ({ role, loginRoute, bottomLine }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login } = useAuthStore();
  const { showToaster } = useToaster();
  const [errorText, setErrorText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { onUsernameChange, resetRoleCheck, isChecking, roleData, roleError } =
    useRoleCheck();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      password: "",
      rememberMe: false,
    },
  });

  const username = watch("username");

  // Effect to trigger role check when username is filled from saved data
  useEffect(() => {
    if (username && username.trim()) {
      const savedUsername = localStorage.getItem('rememberedUsername');
      const isSavedUsername = savedUsername === username;
      
      if (isSavedUsername || username) {
        const timer = setTimeout(() => {
          onUsernameChange(username.trim());
        }, 300);
        
        return () => clearTimeout(timer);
      }
    }
  }, []);

  // Optional: Also trigger when username changes (for real-time validation)
  useEffect(() => {
    if (username && username.trim()) {
      const timer = setTimeout(() => {
        onUsernameChange(username.trim());
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [username, onUsernameChange]);

  const mutation = useMutation({
    mutationFn: (formData) => {
      const resolvedRoute = LOGIN_ROUTE_BY_ROLE[roleData?.role] ?? loginRoute;
      return postLogin(resolvedRoute, formData);
    },
  });

  const handleLogin = (data) => {
    setErrorText("");

    if (roleError) {
      setErrorText("Please enter a valid username before continuing.");
      return;
    }

    const payload = {
      identifier: data.username,
      password: data.password,
    };

    mutation.mutate(payload, {
      onSuccess: (response) => {
        console.log(response?.data, 'response');

        const user = response?.data;
        const token = true;

        if (user) {
          // Save credentials if "Remember Me" is checked
          if (data.rememberMe) {
            localStorage.setItem('rememberedUsername', data.username);
            localStorage.setItem('rememberedPassword', data.password);
            localStorage.setItem('rememberMe', 'true');
          } else {
            localStorage.removeItem('rememberedUsername');
            localStorage.removeItem('rememberedPassword');
            localStorage.removeItem('rememberMe');
          }

          login(user, token);
          console.log(useAuthStore.getState());
          queryClient.prefetchQuery({
            queryKey: ["user-info"],
            queryFn: () => fetchDataNode("details"),
          });
          queryClient.removeQueries({ queryKey: ["courses"], exact: false });
          queryClient.refetchQueries({ queryKey: ["courses"], exact: false });
          const storedPath = localStorage.getItem("lastVisitedPath") || "/";
          navigate(storedPath, { replace: true });
          setTimeout(() => {
            localStorage.removeItem("lastVisitedPath");
          }, 400);

        } else {
          showToaster("Error", "Invalid login response", "error");
        }
      },
      onError: (error) => {
        const status = error.response?.status;
        const message = error.response?.data?.error;

        if (status === 401) {
          setErrorText("Invalid Credentials");
        } else if (message) {
          setErrorText(message);
        } else {
          showToaster("Error", "Something went wrong during login", "error");
        }
      },
    });
  };

  // Load saved credentials on component mount
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (rememberMe && savedUsername) {
      setValue('username', savedUsername);
      if (savedPassword) {
        setValue('password', savedPassword);
      }
      setValue('rememberMe', true);
      
      if (savedUsername.trim()) {
        setTimeout(() => {
          onUsernameChange(savedUsername.trim());
        }, 500);
      }
    }
  }, [setValue, onUsernameChange]);

  const handleSignUpClick = (e) => {
    e.preventDefault();
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <SkillVationAuthLayout
        title="Welcome!!"
        subtitle="One step closer to fun and knowledge!"
        bottomText=""
        bottomLinkLabel=""
        bottomLinkTo=""
      >
        <Box
          component="form"
          onSubmit={handleSubmit(handleLogin)}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          {/* Username */}
          <Box>
            <Typography variant="body2" fontWeight={400} mb={1} color="text.primary">
              Username
            </Typography>
            <motion.div
              animate={errorText ? { x: [-5, 5, -5, 5, 0] } : {}}
              transition={{ duration: 0.3 }}
            >
              <TextField
                fullWidth
                placeholder="Enter your username"
                error={!!errors.username}
                helperText={errors.username?.message}
                {...register("username", { required: "Username is required" })}
                onChange={(e) => {
                  setErrorText("");
                  register("username").onChange(e);
                  const val = e.target.value;
                  if (val.trim()) {
                    onUsernameChange(val);
                  } else {
                    resetRoleCheck();
                  }
                }}
                sx={textFieldStyle}
              />
            </motion.div>
          </Box>

          {/* Password with Eye Button */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight={400} color="text.primary">
                Password
              </Typography>
              <Link to={`/ConfirmMobile/${role}`}>
                <Typography variant="body2" fontSize="12px" color="text.primary">
                  Forgot Password?
                </Typography>
              </Link>
            </Box>
            <motion.div
              animate={errorText ? { x: [-5, 5, -5, 5, 0] } : {}}
              transition={{ duration: 0.3 }}
            >
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                error={!!errors.password}
                helperText={errors.password?.message}
                {...register("password", { required: "Password is required" })}
                onChange={(e) => {
                  setErrorText("");
                  register("password").onChange(e);
                }}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{
                          color: "#9D9D9D",
                          '&:hover': {
                            color: "#4E93E1",
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>
          </Box>

          {/* Login-level error */}
          {errorText && (
            <Typography variant="body2" fontSize="10px" color="#DC143C">
              {errorText}
            </Typography>
          )}

          {/* Remember Me - FIXED VISIBILITY */}
          <FormControlLabel
            control={
              <Checkbox 
                {...register("rememberMe")}
                icon={<CheckBoxOutlineBlankIcon sx={{ color: '#4E93E1', fontSize: 28 }} />}
                checkedIcon={<CheckBoxIcon sx={{ color: '#4E93E1', fontSize: 28 }} />}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 28,
                  },
                  padding: '9px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  }
                }}
              />
            }
            label="Remember me"
            sx={{
              '& .MuiTypography-root': {
                fontSize: '14px',
                fontWeight: 400,
                color: '#666',
              },
              '& .MuiFormControlLabel-label': {
                fontSize: '14px',
                fontWeight: 400,
                color: '#666',
              },
              marginLeft: '-9px',
            }}
          />

          {/* Submit — also disabled while role-check is in-flight */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={mutation.isPending || isChecking}
            sx={submitButtonStyle}
          >
            {mutation.isPending ? "Logging In…" : "Log In"}
          </Button>

          <Grid2 size={12}>
            <OrDivider text="Or" />
          </Grid2>

          <Grid2 size={12}>
            <Grid2 size={12} display="flex" justifyContent="center" gap={0.5}>
              <Typography variant="body2" fontSize="12px">
                If you don't have an account !!
              </Typography>
              <Typography
                variant="body2"
                fontSize="12px"
                color="#2563EB"
                sx={{ cursor: "pointer" }}
                onClick={handleSignUpClick}
              >
                Sign Up
              </Typography>
            </Grid2>
          </Grid2>
        </Box>
      </SkillVationAuthLayout>

      {/* Role Selection Modal */}
      <RoleSelectionModal
        open={modalOpen}
        onClose={handleModalClose}
      />
    </>
  );
};

export default CommonSKillVationLoginForm;